import React, { useState, useEffect, useCallback } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { FaGift, FaPercent, FaRupeeSign, FaTag, FaClock } from 'react-icons/fa';
import './OfferDisplay.css';

const OfferDisplay = ({ productId = null, category = null, showAll = false }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadOffers = useCallback(async () => {
    try {
      const database = getDatabase();
      const offersRef = ref(database, "offers");
      const snapshot = await get(offersRef);
      
      if (snapshot.exists()) {
        const offersData = snapshot.val();
        const offersList = Object.entries(offersData).map(([id, offer]) => ({
          id,
          ...offer
        }));

        // Filter offers based on criteria
        const filteredOffers = offersList.filter(offer => {
          if (!offer.isActive) return false;
          
          const now = new Date();
          const startDate = new Date(offer.startDate);
          const endDate = new Date(offer.endDate);
          
          // Check if offer is currently active
          if (now < startDate || now > endDate) return false;
          
          // Check usage limits
          if (offer.currentUsage >= offer.usageLimit) return false;
          
          // Filter by product if specified
          if (productId && offer.applicableProducts && offer.applicableProducts.length > 0) {
            if (!offer.applicableProducts.includes('all') && 
                !offer.applicableProducts.includes(productId.toString())) {
              return false;
            }
          }
          
          // Filter by category if specified
          if (category && offer.applicableProducts && offer.applicableProducts.length > 0) {
            if (!offer.applicableProducts.includes('all') && 
                !offer.applicableProducts.includes(category)) {
              return false;
            }
          }
          
          return true;
        });
        
        setOffers(filteredOffers);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error loading offers:", error);
      setLoading(false);
    }
  }, [productId, category]);

  useEffect(() => {
    loadOffers();
  }, [loadOffers]);

  const getOfferTypeIcon = (type) => {
    switch (type) {
      case 'percentage':
        return <FaPercent />;
      case 'fixed':
        return <FaRupeeSign />;
      case 'buy-one-get-one':
        return <FaGift />;
      default:
        return <FaTag />;
    }
  };

  const getOfferDisplayText = (offer) => {
    switch (offer.type) {
      case 'percentage':
        return `${offer.value}% OFF`;
      case 'fixed':
        return `₹${offer.value} OFF`;
      case 'buy-one-get-one':
        return 'Buy 2 Get 1 Free';
      default:
        return offer.title;
    }
  };

  const getDaysLeft = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return <div className="offers-loading">Loading offers...</div>;
  }

  if (offers.length === 0) {
    return null;
  }

  return (
    <div className="offers-container">
      {offers.map((offer) => (
        <div key={offer.id} className="offer-banner">
          <div className="offer-icon">
            {getOfferTypeIcon(offer.type)}
          </div>
          <div className="offer-content">
            <div className="offer-title">{offer.title}</div>
            <div className="offer-description">{offer.description}</div>
            <div className="offer-value">{getOfferDisplayText(offer)}</div>
            {offer.minOrderAmount > 0 && (
              <div className="offer-condition">
                Min Order: ₹{offer.minOrderAmount}
              </div>
            )}
          </div>
          <div className="offer-timer">
            <FaClock />
            <span>{getDaysLeft(offer.endDate)} days left</span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default OfferDisplay; 